import { FieldWrapper, IconButton } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { isHotkey } from 'is-hotkey';
import trim from 'lodash/trim';
import {
  ClipboardEvent,
  CSSProperties,
  KeyboardEvent,
  useRef,
  useState,
} from 'react';
import { useEffectOnce } from 'react-use';

import type { FlowCategoriesT } from 'components/powerFlowShared/helpers/flowVariableHelpers';

import { VariableSelector } from './components/variableSelector';
import styles from './tokenInput.module.scss';
import { InputMetaType, InputTypeValueEvent } from './types';
import { getTokenString } from './utils/getTokenString/getTokenString';
import { renderNodeToString } from './utils/renderNodeToString/renderNodeToString';
import { replaceTokensWithTags } from './utils/replaceTokensWithTags/replaceTokensWithTags';

type Props = {
  className?: string;
  input: InputTypeValueEvent<string>;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  label?: string;
  meta?: InputMetaType;
  mode?: 'input' | 'textarea';
  placeholder?: string;
  variables: FlowCategoriesT;
  withVariableSearch?: boolean;
};

export const TokenInput = (props: Props): JSX.Element => {
  const {
    className,
    variables,
    placeholder,
    label,
    input,
    meta,
    inputClassName,
    inputStyle,
    mode,
    withVariableSearch,
    ...otherProps
  } = props;

  const [hasFocus, setHasFocus] = useState(false);
  const [lastSelection, setLastSelection] = useState<Range | null>(null);

  const rootClass = classnames(styles.root, className, {
    [styles.rootFocus]: hasFocus,
    [styles.rootInput]: mode === 'input',
    [styles.rootTextarea]: mode === 'textarea',
  });

  const inputRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isHotkey('mod+b', event) || isHotkey('mod+i', event)) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (mode === 'textarea') {
        document.execCommand('insertLineBreak');
      }
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    const { clipboardData } = event;

    if (!clipboardData) {
      return;
    }

    const text = clipboardData.getData('text/plain');

    document.execCommand('insertHTML', false, text);
  };

  const handleInput = () => {
    const { current } = inputRef;
    if (current) {
      // Chrome execCommand on Chrome insert an extra span with className styles resolved to inline styles.
      // We don't want that element to appear, so we remove it
      current.querySelectorAll('[style]').forEach((el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });

      const text = trim(renderNodeToString(current), '\n');

      input.onChange(text);
    }
  };

  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleBlur = () => {
    const selection = window.getSelection();

    setHasFocus(false);

    if (selection?.rangeCount && selection?.rangeCount > 0) {
      setLastSelection(selection.getRangeAt(0));
    } else {
      setLastSelection(null);
    }
  };

  const handleAddToken = (name: string, label: string) => {
    if (!hasFocus) {
      const { current } = inputRef;

      if (current) {
        if (lastSelection) {
          const selection = window.getSelection();

          if (selection === null) {
            return;
          }

          selection.removeAllRanges();
          selection.addRange(lastSelection);
        } else {
          current.focus();
        }
      }
    }

    document.execCommand('insertHTML', false, getTokenString(name, label));
  };

  useEffectOnce(() => {
    const { current } = inputRef;

    if (current) {
      const temp = document.createElement('div');
      temp.textContent = input.value;

      // This is perfectly sanitary.
      // eslint-disable-next-line no-unsanitized/property
      current.innerHTML = replaceTokensWithTags(temp.innerHTML, variables);
    }
  });

  return (
    <FieldWrapper
      {...otherProps}
      className={rootClass}
      label={label}
      meta={meta}
    >
      <div className={styles.wrapper}>
        <div className={classnames(styles.input, styles.inputAligned)}>
          <div
            style={inputStyle}
            id="input"
            className={classnames(
              inputClassName,
              styles.inputInner,
              styles.inputInnerAligned,
            )}
            contentEditable
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={inputRef}
            role="textbox"
            tabIndex={0}
          />
          {input.value.length === 0 && !!placeholder && (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>

        <VariableSelector
          onSelect={handleAddToken}
          variables={variables}
          className={styles.dropdownSelect}
          withSearch={withVariableSearch}
        >
          <IconButton
            icon="dropdown-list"
            variant="tertiary"
            className={styles.addButton}
          />
        </VariableSelector>
      </div>
    </FieldWrapper>
  );
};
